// Promotion-channel + painpoint capture for acquisition attribution.
//
// Two campaign URL shapes land here:
//   - `https://<site>/c/<channel>`     — the original channel link.
//   - `https://<site>/lp/<painpoint>?utm_source=<platform>&utm_campaign=...`
//                                       — a painpoint landing page (migration
//                                         029). The painpoint slug drives the
//                                         landing content AND is the primary
//                                         attribution key (it flows to the
//                                         backend as `channel`); the platform
//                                         rides along via utm_source.
//
// On landing we pull the slug (and any utm_* params) out of the URL and stash
// them in localStorage so they survive the visitor leaving and coming back
// before tapping "Message Ember". This was sessionStorage originally, which
// silently dropped attribution on mobile: the verify flow hands off to the
// Messages app, and when the visitor returns the in-app browser has usually
// killed the tab — a fresh session, empty storage, unattributed signup.
// Capture is first-touch with a 30-day expiry: the first campaign seen wins
// and is never overwritten, mirroring the backend's first-touch attribution
// write.

const STORAGE_KEY = 'ori_channel';
const ATTRIB_KEY = 'ori_attrib';
const FIRST_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// In-memory first-touch cache, checked before — and populated alongside —
// localStorage. Paid social traffic lands inside in-app browsers (Facebook /
// TikTok webviews) that partition or disable localStorage, so a value written
// at the /lp landing was invisible to the read at handoff time and attribution
// silently dropped for ~every ad click. The "Message Ember" CTA + handoff run on
// the SAME page load as the landing, so this cache makes capture -> read
// reliable even when storage is blocked; localStorage stays as the
// cross-session / return-trip backup.
const memory: Record<string, string> = {};

// localStorage values are wrapped as {v, exp} so stale campaigns age out.
// Reads also check the legacy sessionStorage location (pre-TTL format: the
// bare value) and promote it, so a visitor mid-flow during the rollout keeps
// their attribution.
function readPersisted(key: string): string {
  if (memory[key]) return memory[key];
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { v?: unknown; exp?: unknown };
        if (parsed && typeof parsed.v === 'string') {
          if (typeof parsed.exp === 'number' && parsed.exp < Date.now()) {
            localStorage.removeItem(key);
            return '';
          }
          memory[key] = parsed.v;
          return parsed.v;
        }
      } catch {
        /* not the wrapped format — fall through to legacy handling */
      }
      memory[key] = String(raw);
      return String(raw);
    }
    const legacy = sessionStorage.getItem(key) || '';
    if (legacy) writePersisted(key, legacy);
    return legacy;
  } catch {
    return '';
  }
}

function writePersisted(key: string, value: string): void {
  // In-memory first so attribution survives this page load even if the write
  // below throws (private mode / in-app webview with storage disabled).
  memory[key] = value;
  try {
    localStorage.setItem(key, JSON.stringify({ v: value, exp: Date.now() + FIRST_TOUCH_TTL_MS }));
  } catch {
    /* storage disabled — attribution survives in-memory for this page load */
  }
}
const CHANNEL_RE = /^\/c\/([a-z0-9][a-z0-9_-]{0,39})$/;
const PAINPOINT_RE = /^\/lp\/([a-z0-9][a-z0-9_-]{0,39})\/?$/;
// Theme landing (/themes/<slug>, Theme-Driven v0.6): the theme slug is an entry
// attribution key exactly like a painpoint slug — it doubles as channel and
// additionally rides its own `theme` field so the signup can stamp
// entry_theme_id on the interview.
const THEME_RE = /^\/themes\/([a-z0-9][a-z0-9_-]{0,39})\/?$/;

export type Utm = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
};

export type Attribution = {
  channel: string;
  painpoint?: string;
  theme?: string;
  platform?: string;
  solution?: string;
  utm?: Utm;
  captured_at: string;
};

// The Ember journey a painpoint campaign promotes, carried in the URL as
// ?solution=<plan|invite>. Anything else is ignored (brain falls back to the
// open-ended question). Keep in sync with edge channel.js PAINPOINT_SOLUTIONS.
// NOTE: for painpoints in the edge-side hardcoded map (PAINPOINT_TO_SOLUTION)
// the server DERIVES the solution from the painpoint at capture — this URL
// value is only a fallback for unmapped slugs (and is normalized through the
// same map in the admin funnel query, so click rows can't fragment).
const SOLUTIONS = ['plan', 'invite'];
function readSolution(): string {
  try {
    const v = (new URLSearchParams(window.location.search).get('solution') || '')
      .trim()
      .toLowerCase();
    return SOLUTIONS.includes(v) ? v : '';
  } catch {
    return '';
  }
}

function safeGet(): string {
  return readPersisted(STORAGE_KEY);
}

function safeSet(value: string): void {
  writePersisted(STORAGE_KEY, value);
}

function safeGetAttrib(): Attribution | null {
  try {
    const raw = readPersisted(ATTRIB_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

function safeSetAttrib(value: Attribution): void {
  try {
    writePersisted(ATTRIB_KEY, JSON.stringify(value));
  } catch {
    /* best-effort */
  }
}

// Same shape as the backend slugifyPlatform / slugifyChannel rule, applied
// client-side so the stored platform is already clean; the server re-slugifies
// defensively regardless.
function slugify(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s./]+/g, '-')
    .replace(/[^a-z0-9_-]/g, '')
    .replace(/^[-_]+|[-_]+$/g, '');
}

function readUtm(): Utm | undefined {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return undefined;
  }
  const out: Utm = {};
  (['source', 'medium', 'campaign', 'content', 'term'] as const).forEach((k) => {
    const v = (params.get(`utm_${k}`) || '').trim().slice(0, 80);
    if (v) out[k] = v;
  });
  return Object.keys(out).length > 0 ? out : undefined;
}

// The ad platforms' click ids (?ttclid= / ?fbclid=) are deliberately NOT
// captured here any more. This module used to stash both in localStorage, but
// nothing ever read them back: the server matches conversions on the pixels'
// own first-party cookies, read off the request (src/edge/auth/cookie.js), and
// the head snippets copy ttclid into a cookie INSIDE the consent-gated pixel
// load — which is the property that matters, and one an unconditional capture
// on page load cannot have. What was here was a leftover of the pre-Ember
// funnel; do not reintroduce it, or "cookie present" stops meaning "consent
// was given".

// Call once on app boot. Records first-touch attribution from /lp/<painpoint>,
// /c/<channel>, or any path carrying utm_* params. Never overwrites an existing
// capture.
export function captureChannelFromPath(): void {
  const path = String(window.location.pathname || '').toLowerCase();
  const painpointMatch = path.match(PAINPOINT_RE);
  const channelMatch = path.match(CHANNEL_RE);
  const themeMatch = path.match(THEME_RE);

  if (themeMatch) {
    const slug = themeMatch[1];
    if (!safeGet()) safeSet(slug); // theme slug doubles as the channel key
    if (!safeGetAttrib()) {
      const utm = readUtm();
      const platform = slugify(utm?.source || '').slice(0, 24);
      safeSetAttrib({
        channel: slug,
        theme: slug,
        ...(platform ? { platform } : {}),
        ...(utm ? { utm } : {}),
        captured_at: new Date().toISOString(),
      });
    }
    return;
  }

  if (painpointMatch) {
    const slug = painpointMatch[1];
    if (!safeGet()) safeSet(slug); // back-compat: channel == painpoint slug
    if (!safeGetAttrib()) {
      const utm = readUtm();
      const platform = slugify(utm?.source || '').slice(0, 24);
      const solution = readSolution();
      safeSetAttrib({
        channel: slug,
        painpoint: slug,
        ...(platform ? { platform } : {}),
        ...(solution ? { solution } : {}),
        ...(utm ? { utm } : {}),
        captured_at: new Date().toISOString(),
      });
    }
    return;
  }

  if (channelMatch) {
    const slug = channelMatch[1];
    if (!safeGet()) safeSet(slug);
    if (!safeGetAttrib()) {
      const utm = readUtm();
      safeSetAttrib({
        channel: slug,
        ...(utm ? { utm } : {}),
        captured_at: new Date().toISOString(),
      });
    }
    return;
  }

  // Bare-path fallback: a paid ad can land on the homepage (or any non-slug
  // path) carrying only utm_* params — e.g. `/?utm_source=facebook&utm_campaign=hp`.
  // Without this the function stores nothing and the signup is unattributed.
  // Capture a utm-keyed first-touch record so platform + campaign still reach
  // the backend. utm_campaign mirrors the painpoint slug (hp, dhp, ...), so it
  // doubles as the channel key when no explicit slug is in the path.
  if (!safeGetAttrib()) {
    const utm = readUtm();
    const platform = slugify(utm?.source || '').slice(0, 24);
    const campaign = slugify(utm?.campaign || '').slice(0, 40);
    const channel = campaign || platform;
    if (channel) {
      const solution = readSolution();
      if (!safeGet()) safeSet(channel);
      safeSetAttrib({
        channel,
        ...(platform ? { platform } : {}),
        ...(solution ? { solution } : {}),
        ...(utm ? { utm } : {}),
        captured_at: new Date().toISOString(),
      });
    }
  }
}

// The captured channel slug, or '' if none. Safe to call anywhere.
export function getStoredChannel(): string {
  return safeGet();
}

// The full first-touch attribution record (channel + painpoint + platform +
// utm), or null if none was captured. Safe to call anywhere.
export function getStoredAttribution(): Attribution | null {
  return safeGetAttrib();
}
